import React, { useState } from 'react';
import { apiService } from '../services/api';
import { ProcessResponse } from '../types';
import './BatchProcessor.css';

interface BatchProcessorProps {
  selectedEntryIds: string[];
  onProcessComplete: (processedIds: string[]) => void;
}

const BatchProcessor: React.FC<BatchProcessorProps> = ({
  selectedEntryIds,
  onProcessComplete
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleProcess = async () => {
    if (selectedEntryIds.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiService.processPreEntries({
        pre_entry_ids: selectedEntryIds
      });

      setResult(response);

      if (response.processed_count > 0) {
        // Calculate which entries were successfully processed
        const processedIds = selectedEntryIds.filter(
          id => !response.failed_ids?.includes(id)
        );
        onProcessComplete(processedIds);
      }

      // Clear result after 5 seconds if successful
      if (response.processed_count === selectedEntryIds.length) {
        setTimeout(() => {
          setResult(null);
        }, 5000);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const hasPartialFailure = result && result.failed_ids && result.failed_ids.length > 0;
  const isFullSuccess = result && result.processed_count === selectedEntryIds.length;
  const isFullFailure = result && result.processed_count === 0;

  return (
    <div className="batch-processor">
      <div className="processor-header">
        <h3>Process Selected Entries</h3>
        <div className="selected-count">
          {selectedEntryIds.length} entries selected
        </div>
      </div>

      <div className="processor-content">
        <p className="processor-description">
          Convert selected pre-entries to final entries in your expense tracker. 
          Make sure all entries have categories and sections assigned.
        </p>

        <div className="processor-actions">
          <button
            onClick={handleProcess}
            disabled={isProcessing || selectedEntryIds.length === 0}
            className="process-button"
          >
            {isProcessing ? 'Processing...' : `Process ${selectedEntryIds.length} Entries`}
          </button>
        </div>

        {/* Processing Status */}
        {isProcessing && (
          <div className="processing-status">
            <div className="progress-indicator">
              <div className="spinner"></div>
              <span>Processing entries...</span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="processor-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="processor-results">
            {isFullSuccess && (
              <div className="result-success">
                ✅ Successfully processed all {result.processed_count} entries!
              </div>
            )}

            {hasPartialFailure && (
              <div className="result-partial">
                <div className="partial-summary">
                  ⚠️ Partial success: {result.processed_count} of {selectedEntryIds.length} entries processed
                </div>
                
                {result.failed_ids && result.failed_ids.length > 0 && (
                  <details className="failed-details">
                    <summary>
                      {result.failed_ids.length} entries failed
                    </summary>
                    <div className="failed-list">
                      {result.errors?.map((error, index) => (
                        <div key={index} className="failed-item">
                          {error}
                        </div>
                      )) || (
                        <div>Failed entry IDs: {result.failed_ids.join(', ')}</div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}

            {isFullFailure && (
              <div className="result-failure">
                ❌ Failed to process any entries
                {result.errors && result.errors.length > 0 && (
                  <details className="error-details">
                    <summary>View errors</summary>
                    <div className="error-list">
                      {result.errors.map((error, index) => (
                        <div key={index} className="error-item">
                          {error}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchProcessor;